import { createReducer, createSelector } from '@reduxjs/toolkit';

import type {
	PendingFriendRequest,
	PlayerFriendshipStatus,
	PlayersDirectoryFilter,
	PlayersDirectoryItem,
} from '../../services/api';
import { logout } from '../auth/authActions';
import {
	directoryFailure,
	directoryRequest,
	directorySuccess,
	friendActionFailure,
	friendActionSuccess,
	friendRemove,
	friendRequestRespond,
	friendRequestSend,
	friendsListFailure,
	friendsListRequest,
	friendsListSuccess,
	pendingRequestsFailure,
	pendingRequestsRequest,
	pendingRequestsSuccess,
} from './friendsActions';

/**
 * What this session's own send / accept / reject / remove settled to. The read side is a CDC
 * projection, so a list fetched right after a write can still show the old relationship; the
 * override covers that gap and gives way once a fetch has had time to catch up.
 */
type RelationOverride = {
	status: PlayerFriendshipStatus;
	friendRequestId: string | null;
	at: number;
};

const OVERRIDE_TTL_MS = 5000;

export type FriendsState = {
	directory: {
		filter: PlayersDirectoryFilter;
		query: string;
		items: PlayersDirectoryItem[];
		totalCount: number;
		loading: boolean;
		loadingMore: boolean;
		errorKey?: string;
	};
	friends: {
		query: string;
		items: PlayersDirectoryItem[];
		/** The player's friends count, independent of the search box. */
		friendsCount: number | null;
		loading: boolean;
		errorKey?: string;
	};
	pending: {
		items: PendingFriendRequest[];
		totalCount: number;
		loading: boolean;
	};
	relationByPlayerId: Record<string, RelationOverride>;
	busyPlayerIds: Record<string, true>;
	actionError: { playerId: string; message: string } | null;
};

const initial: FriendsState = {
	directory: {
		filter: 'FRIENDS',
		query: '',
		items: [],
		totalCount: 0,
		loading: false,
		loadingMore: false,
	},
	friends: { query: '', items: [], friendsCount: null, loading: false },
	pending: { items: [], totalCount: 0, loading: false },
	relationByPlayerId: {},
	busyPlayerIds: {},
	actionError: null,
};

const dropSettledOverrides = (s: FriendsState) => {
	const now = Date.now();
	for (const [playerId, override] of Object.entries(s.relationByPlayerId)) {
		if (now - override.at > OVERRIDE_TTL_MS) delete s.relationByPlayerId[playerId];
	}
};

const beginAction = (s: FriendsState, playerId: string) => {
	s.busyPlayerIds[playerId] = true;
	s.actionError = null;
};

export const friendsReducer = createReducer(initial, b => {
	b.addCase(directoryRequest, (s, a) => {
		s.directory.filter = a.payload.filter;
		s.directory.query = a.payload.query;
		s.directory.errorKey = undefined;
		if (a.payload.append) {
			s.directory.loadingMore = true;
		} else {
			s.directory.loading = true;
		}
	});
	b.addCase(directorySuccess, (s, a) => {
		dropSettledOverrides(s);
		s.directory.loading = false;
		s.directory.loadingMore = false;
		s.directory.totalCount = a.payload.response.totalCount;
		s.directory.items = a.payload.append
			? [...s.directory.items, ...a.payload.response.items]
			: a.payload.response.items;
	});
	b.addCase(directoryFailure, (s, a) => {
		s.directory.loading = false;
		s.directory.loadingMore = false;
		s.directory.errorKey = a.payload;
	});

	b.addCase(friendsListRequest, (s, a) => {
		s.friends.query = a.payload.query;
		s.friends.loading = true;
		s.friends.errorKey = undefined;
	});
	b.addCase(friendsListSuccess, (s, a) => {
		dropSettledOverrides(s);
		s.friends.loading = false;
		s.friends.items = a.payload.response.items;
		if (!a.payload.query) s.friends.friendsCount = a.payload.response.totalCount;
	});
	b.addCase(friendsListFailure, (s, a) => {
		s.friends.loading = false;
		s.friends.errorKey = a.payload;
	});

	b.addCase(pendingRequestsRequest, s => {
		s.pending.loading = true;
	});
	b.addCase(pendingRequestsSuccess, (s, a) => {
		s.pending.loading = false;
		s.pending.items = a.payload.items;
		s.pending.totalCount = a.payload.totalCount;
	});
	b.addCase(pendingRequestsFailure, s => {
		s.pending.loading = false;
	});

	b.addCase(friendRequestSend, (s, a) => beginAction(s, a.payload.playerId));
	b.addCase(friendRequestRespond, (s, a) => beginAction(s, a.payload.playerId));
	b.addCase(friendRemove, (s, a) => beginAction(s, a.payload.playerId));

	b.addCase(friendActionSuccess, (s, a) => {
		const { playerId, status, friendRequestId } = a.payload;
		const wasFriend = s.friends.items.some(friend => friend.id === playerId);

		delete s.busyPlayerIds[playerId];
		s.relationByPlayerId[playerId] = { status, friendRequestId, at: Date.now() };

		if (status !== 'PENDING_INCOMING') {
			s.pending.items = s.pending.items.filter(request => request.requesterId !== playerId);
			s.pending.totalCount = s.pending.items.length;
		}
		if (status !== 'FRIENDS' && wasFriend) {
			s.friends.items = s.friends.items.filter(friend => friend.id !== playerId);
			if (s.friends.friendsCount) s.friends.friendsCount -= 1;
		}
	});
	b.addCase(friendActionFailure, (s, a) => {
		delete s.busyPlayerIds[a.payload.playerId];
		s.actionError = a.payload;
	});

	b.addCase(logout, () => initial);
});

const applyOverride = (
	item: PlayersDirectoryItem,
	overrides: FriendsState['relationByPlayerId'],
): PlayersDirectoryItem => {
	const override = overrides[item.id];
	if (!override) return item;

	return { ...item, friendshipStatus: override.status, friendRequestId: override.friendRequestId };
};

type WithFriends = { friends: FriendsState };

const selectFriends = (state: WithFriends) => state.friends;

export const selectDirectory = createSelector(selectFriends, friends => ({
	...friends.directory,
	items: friends.directory.items.map(item => applyOverride(item, friends.relationByPlayerId)),
}));

export const selectFriendsList = createSelector(selectFriends, friends => ({
	...friends.friends,
	items: friends.friends.items
		.map(item => applyOverride(item, friends.relationByPlayerId))
		.filter(item => item.friendshipStatus === 'FRIENDS'),
}));

export const selectPendingRequests = (state: WithFriends) => state.friends.pending;
export const selectFriendBusyIds = (state: WithFriends) => state.friends.busyPlayerIds;
export const selectFriendBusy = (state: WithFriends, playerId: string) =>
	!!state.friends.busyPlayerIds[playerId];
export const selectFriendRelationOverride = (state: WithFriends, playerId: string) =>
	state.friends.relationByPlayerId[playerId];
export const selectFriendActionError = (state: WithFriends) => state.friends.actionError;
